import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import { Script } from 'gatsby';
import { GlobalStyles } from '@utils/GlobalStyles';
import styled from 'styled-components';
import Head from '@repo/ui-components/Head';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import SkipToContent from '@repo/ui-components/skip-to-content';
import Cart from '@repo/ui-components/cart/Cart';
import Toast from '@repo/ui-components/cart/Toast';
import { InitInsights } from '@repo/utils/algoliaAnalytics';
import { localeToKlaviyoPublicMap } from '@repo/utils/klaviyo';
import { CartContext } from '@repo/contexts/CartContext';
import styles from '@utils/styles.js';
import LayoutHeader from '@components/layout/LayoutHeader';
import { useSportdogInternationalContext } from '@util/static-translations';
import { GLOBAL_LOCALES } from '@repo/gatsby-localization-helpers';
import Footer from '../footer/Footer';
import '../../styles/globals.css';
import 'react-image-gallery/styles/css/image-gallery.css';
import 'lite-youtube-embed/src/lite-yt-embed.css';

const CartWarning = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: ${styles.colors.gray[500]};
  padding: 0.5rem;
  font-size: 1rem;
`;

if (process.env.GATSBY_SANITY_PREVIEW_DEPLOY === 'true') {
  import('../../styles/web-preview.css');
}

function Layout({
  children,
  headTagData,
  scriptsData,
  fullWidth,
  linkHreflangValues,
}) {
  const { cartError } = useContext(CartContext);
  const { tt, locale } = useSportdogInternationalContext();

  const {
    title,
    description,
    keywords,
    meta,
    productSchema,
    includeBVScript,
    canonicalUrl,
    noIndex,
    noFollow,
  } = headTagData;

  InitInsights();

  const links = [];

  if (canonicalUrl) {
    links.push(<link key="canonical" rel="canonical" href={canonicalUrl} />);
  }

  return (
    <>
      <Head
        title={title}
        description={description}
        keywords={keywords}
        meta={meta}
        schemaOrgObj={productSchema}
        linkTags={links}
        scriptTags={scriptsData}
        noIndex={noIndex}
        noFollow={noFollow}
        lang={locale}
        hreflangs={linkHreflangValues}
      >
        {locale === GLOBAL_LOCALES.English.id && (
          <script key="google-tag-manager" async>
            {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${process.env.GATSBY_GTM_ID}');`}
          </script>
        )}
      </Head>
      <SkipToContent />
      <GlobalStyles />
      <Cart
        highlightColor={styles.colors.orange[500]}
        headerBackgroundColor={styles.colors.gray[600]}
        textColor={styles.colors.gray[500]}
      />
      <Toast
        headerBackgroundColor={styles.colors.gray[600]}
        headerTextColor={styles.colors.white}
        textColor={styles.colors.gray[500]}
      />
      {cartError && (
        <CartWarning>
          {tt(s => s.layoutCommon.cantPurchaseWarningMessage)}
        </CartWarning>
      )}
      <LayoutHeader />
      <PageWrapper fullWidth={fullWidth} padding="0">
        <main id="content">{children}</main>
      </PageWrapper>
      <LazyLoad offset={100}>
        <Footer />
      </LazyLoad>
      {process.env.GATSBY_COOKIEPRO_SITE_ID && [
        <Script
          async
          key="otSDKStub"
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          data-domain-script={process.env.GATSBY_COOKIEPRO_SITE_ID}
          data-document-language="true"
          data-culture={locale}
        />,
      ]}

      {localeToKlaviyoPublicMap(locale, process.env.GATSBY_KLAVIYO_BRAND)
        .publicKey && (
        <Script
          key="klaviyo"
          async
          src={`https://static.klaviyo.com/onsite/js/${
            localeToKlaviyoPublicMap(locale, process.env.GATSBY_KLAVIYO_BRAND)
              .publicKey
          }/klaviyo.js`}
        />
      )}
      {includeBVScript && (
        <Script
          defer
          key="bazaarvoice"
          src={`https://apps.bazaarvoice.com/deployments/sportdog/main_site/${process.env.GATSBY_BAZAARVOICE_ENVIRONMENT}/en_US/bv.js`}
        />
      )}
      {locale === GLOBAL_LOCALES.English.id && (
        <Script
          key="mavenoid"
          defer
          src="https://widget-hosts.mavenoid.com/custom-embedding-scripts/radio-systems-corp.js"
          onLoad={() => {
            window.mavenoid = window?.mavenoid || [];

            // hide mavenoid chat ui behind cookiepro banner
            const observer = new MutationObserver(
              (mutations, mutationInstance) => {
                const mavenoidComponent =
                  document.querySelector('mavenoid-assistant');
                if (mavenoidComponent) {
                  mavenoidComponent.style.zIndex = 5;
                  mutationInstance.disconnect();
                }
              }
            );

            observer.observe(document, {
              childList: true,
              subtree: true,
            });
          }}
        />
      )}
    </>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  headTagData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    keywords: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.shape({})),
    productSchema: PropTypes.string,
    includeBVScript: PropTypes.bool,
    canonicalUrl: PropTypes.string,
    noIndex: PropTypes.bool,
    noFollow: PropTypes.bool,
  }),
  messageSvg: PropTypes.shape({
    linkType: PropTypes.oneOf(['picked', 'upload', 'disable']),
    svgUploaded: PropTypes.oneOf([null]),
  }),
  fullWidth: PropTypes.bool,
  scriptsData: PropTypes.arrayOf(PropTypes.shape({})),
  linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
};

Layout.defaultProps = {
  headTagData: {},
  fullWidth: false,
  scriptsData: [],
  messageSvg: {},
  linkHreflangValues: [],
};

export default Layout;
